import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { StoreConfig } from '../hooks/useStoreConfig';
import { useStoreConfig } from '../hooks/useStoreConfig';

interface StoreConfigContextValue {
  config: StoreConfig | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  subscribe: () => () => void;
}

const StoreConfigContext = createContext<StoreConfigContextValue | undefined>(undefined);

interface StoreConfigProviderProps {
  children: React.ReactNode;
  storeId?: string;
}

export const StoreConfigProvider: React.FC<StoreConfigProviderProps> = ({ children, storeId }) => {
  const subscribersRef = useRef<number>(0);
  const [shouldFetch, setShouldFetch] = useState(false);
  const hasFetchedRef = useRef(false);

  // Only fetch when we have subscribers
  const { config, loading, error, refetch } = useStoreConfig({
    autoFetch: shouldFetch,
  });

  const subscribe = () => {
    subscribersRef.current += 1;

    // If this is the first subscriber, trigger fetch
    if (subscribersRef.current === 1 && !hasFetchedRef.current) {
      setShouldFetch(true);
      hasFetchedRef.current = true;
    }

    // Return unsubscribe function
    return () => {
      subscribersRef.current -= 1;
    };
  };

  const value: StoreConfigContextValue = {
    config,
    loading,
    error,
    refetch,
    subscribe,
  };

  return <StoreConfigContext.Provider value={value}>{children}</StoreConfigContext.Provider>;
};

/**
 * Hook to access store config context
 * This hook automatically subscribes to config when the component mounts
 * and unsubscribes when it unmounts
 */
export const useStoreConfigContext = () => {
  const context = useContext(StoreConfigContext);

  if (!context) {
    throw new Error('useStoreConfigContext must be used within a StoreConfigProvider');
  }

  const { subscribe, ...rest } = context;

  // Automatically subscribe/unsubscribe
  useEffect(() => {
    const unsubscribe = subscribe();
    return unsubscribe;
  }, []);

  return rest;
};

/**
 * Hook to access store config context without automatic subscription
 * Use this if you need to manually control when config is fetched
 */
export const useStoreConfigContextRaw = () => {
  const context = useContext(StoreConfigContext);

  if (!context) {
    throw new Error('useStoreConfigContextRaw must be used within a StoreConfigProvider');
  }

  return context;
};

/**
 * Safe version that returns null instead of throwing
 * Use this when the provider might not be available
 */
export const useStoreConfigContextSafe = () => {
  const context = useContext(StoreConfigContext);
  return context || null;
};
